import { useEffect, useRef } from "react";
import { useAuthStore } from "../stores/authStore";

// Refresh 5 minutes before expiration
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

/**
 * Hook that sets up a timer to proactively refresh the access token
 * before it expires. This prevents users from being logged out unexpectedly.
 */
export function useTokenRefreshTimer() {
  const session = useAuthStore((state) => state.session);
  const refreshSession = useAuthStore((state) => state.refreshSession);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    // Clear existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // No session or missing refresh data
    if (!session?.expires_at || !session?.refresh_token) {
      return;
    }

    // Calculate time until we should refresh
    const expiresAtMs = session.expires_at * 1000; // Supabase returns seconds
    const now = Date.now();
    const timeUntilRefresh = expiresAtMs - now - REFRESH_BUFFER_MS;

    if (timeUntilRefresh <= 0) {
      // Already time to refresh
      refreshSession();
    } else {
      // Schedule refresh
      timerRef.current = window.setTimeout(() => {
        refreshSession();
      }, timeUntilRefresh);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [session?.expires_at, session?.refresh_token, refreshSession]);
}
